<i18n lang="yaml" src="@/locales/dialog.yaml"></i18n>

<i18n lang="yaml">
en:
  verification_code_title: Two phase authentication
  verification_code_text: For extra protection of your account, please enter the verification code we just sent you to "{email}" ({codeId}).
  code_id: request ID {codeId}
  login_another_user: Login as another user
  verify_code: Verify code
  code: code
  failed_to_generate: Failed to generate verification code.
  wrong_code: The code you entered is not valid.
  resend_verification_code: Resend email
  verification_email_successfully_sent: Verification email was successfully sent.
  select_device: Select verification method
  totp_instructions: Open your authenticator app and enter the 6-digit code for "{name}".
  totp_wrong_code: The code is not valid or has expired. Please try again.
  too_many_attempts: Too many failed attempts. You can try again in {time}.
  webauthn_label: Security key / passkey
  webauthn_text: Use your security key or passkey to continue.
  verify_webauthn: Use security key
  webauthn_failed: Security key verification failed. Please try again or use another method.
  webauthn_unsupported: Your browser does not support passkeys / security keys.
cs:
  verification_code_title: Dvoufázová autentizace
  verification_code_text: Pro extra ochranu vašeho účtu, vložte prosím ověřovací kód, který jsme vám právě poslali na "{email}" ({codeId}).
  code_id: ID žádosti {codeId}
  login_another_user: Přihlásit jiného uživatele
  verify_code: Ověřit kód
  code: kód
  failed_to_generate: Nepodařilo se vytvořit kód.
  wrong_code: Kód, který byl použit není platný.
  resend_verification_code: Znovu poslat
  verification_email_successfully_sent: Ověřovací email byl úspěšně odeslán.
  select_device: Vyberte způsob ověření
  totp_instructions: Otevřete svou autentizační aplikaci a zadejte 6ciferný kód pro "{name}".
  totp_wrong_code: Kód není platný nebo vypršela jeho platnost. Zkuste to znovu.
  too_many_attempts: Příliš mnoho neúspěšných pokusů. Další pokus bude možný za {time}.
  webauthn_label: Bezpečnostní klíč / passkey
  webauthn_text: Pro pokračování použijte bezpečnostní klíč nebo passkey.
  verify_webauthn: Použít bezpečnostní klíč
  webauthn_failed: Ověření bezpečnostním klíčem selhalo. Zkuste to znovu nebo použijte jiný způsob.
  webauthn_unsupported: Váš prohlížeč nepodporuje passkeys / bezpečnostní klíče.
</i18n>

<template>
  <v-dialog v-model="otpRequired" persistent max-width="520">
    <v-form>
      <v-card>
        <v-card-title class="headline">{{
          $t("verification_code_title")
        }}</v-card-title>
        <v-card-text>
          <!-- Device selector (shown when more than one device is available) -->
          <div v-if="allDevices.length > 1" class="mb-4">
            <p class="text-caption mb-1">{{ $t("select_device") }}</p>
            <v-list density="compact" nav>
              <v-list-item
                v-for="device in allDevices"
                :key="`${device.device_type}-${device.pk}`"
                :active="isSelected(device)"
                active-color="primary"
                :prepend-icon="deviceIcon(device)"
                :title="deviceLabel(device)"
                @click="selectDevice(device)"
              ></v-list-item>
            </v-list>
            <v-divider class="mb-4"></v-divider>
          </div>

          <!-- EMAIL branch -->
          <template v-if="activeDeviceType === 'email'">
            <div>
              <i18n-t keypath="verification_code_text">
                <template #email>
                  <span class="font-weight-medium">{{
                    selectedDevice.email
                  }}</span>
                </template>
                <template #codeId>
                  <span class="caption">{{
                    $t("code_id", { codeId: codeRequestId })
                  }}</span>
                </template>
              </i18n-t>
            </div>
            <div v-if="codeRequestId">
              <div class="d-flex pt-6">
                <v-otp-input
                  v-model="code"
                  :label="$t('code')"
                  type="number"
                ></v-otp-input>
                <v-btn
                  color="primary"
                  class="ma-3 align-self-center"
                  type="submit"
                  :disabled="
                    working || otpLocked || !codeRequestId || code.length != 6
                  "
                  @click="verifyEmailCode"
                >
                  {{ $t("verify_code") }}
                </v-btn>
              </div>
            </div>
            <CelusErrorAlert v-if="otpLocked">
              {{ $t("too_many_attempts", { time: otpLockRemaining }) }}
            </CelusErrorAlert>
            <CelusErrorAlert v-else-if="genError">
              {{ $t("failed_to_generate") }}
            </CelusErrorAlert>
            <CelusErrorAlert v-else-if="codeError">
              {{ $t("wrong_code") }}
            </CelusErrorAlert>
          </template>

          <!-- TOTP branch -->
          <template v-else-if="activeDeviceType === 'totp'">
            <div class="mb-4">
              <i18n-t keypath="totp_instructions">
                <template #name>
                  <span class="font-weight-medium">{{
                    selectedDevice.name
                  }}</span>
                </template>
              </i18n-t>
            </div>
            <div class="d-flex pt-2">
              <v-otp-input
                v-model="code"
                :label="$t('code')"
                type="number"
                autofocus
              ></v-otp-input>
              <v-btn
                color="primary"
                class="ma-3 align-self-center"
                type="submit"
                :disabled="working || otpLocked || code.length != 6"
                @click="verifyTotpCode"
              >
                {{ $t("verify_code") }}
              </v-btn>
            </div>
            <CelusErrorAlert v-if="otpLocked">
              {{ $t("too_many_attempts", { time: otpLockRemaining }) }}
            </CelusErrorAlert>
            <CelusErrorAlert v-else-if="codeError">
              {{ $t("totp_wrong_code") }}
            </CelusErrorAlert>
          </template>

          <!-- WebAuthn branch -->
          <template v-else-if="activeDeviceType === 'webauthn'">
            <p>{{ $t("webauthn_text") }}</p>
            <div class="d-flex pt-4">
              <v-btn
                color="primary"
                :disabled="working || !webAuthnSupported"
                :loading="working"
                @click="verifyWebAuthn"
              >
                <v-icon class="me-2" size="small" :icon="$icons.key" />
                {{ $t("verify_webauthn") }}
              </v-btn>
            </div>
            <CelusWarningAlert v-if="!webAuthnSupported" class="mt-4">
              {{ $t("webauthn_unsupported") }}
            </CelusWarningAlert>
            <CelusErrorAlert v-else-if="webauthnError" class="mt-4">
              {{ $t("webauthn_failed") }}
            </CelusErrorAlert>
          </template>
        </v-card-text>
        <v-card-actions>
          <!-- Show resend button only for email -->
          <v-btn
            v-if="activeDeviceType === 'email'"
            color="success"
            class="ma-3"
            :disabled="working || !codeRequestId"
            @click="sendCodeViaEmail"
            variant="elevated"
          >
            <v-icon class="me-2" :icon="$icons.email" />
            {{ $t("resend_verification_code") }}
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            class="ma-3"
            type="submit"
            @click="logout"
            :disabled="working"
            variant="elevated"
          >
            <v-icon class="me-2" :icon="$icons.user" />
            {{ $t("login_another_user") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
    <!-- this dialog is shown outside of the normal layout, so it needs its own snackbar -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color">
      {{ snackbar.content }}
    </v-snackbar>
  </v-dialog>
</template>

<script>
import CelusErrorAlert from "@/components/util/CelusErrorAlert.vue";
import CelusWarningAlert from "@/components/util/CelusWarningAlert.vue";
import {
  isWebAuthnCancellation,
  isWebAuthnSupported,
  webAuthnAuthenticate,
} from "@/libs/webauthn";
import cancellation from "@/mixins/cancellation";
import formRulesMixin from "@/mixins/formRulesMixin";
import otpThrottle from "@/mixins/otpThrottle";
import { mapActions, mapState } from "vuex";

export default {
  name: "OtpDialog",
  components: { CelusErrorAlert, CelusWarningAlert },

  mixins: [cancellation, formRulesMixin, otpThrottle],

  props: {
    value: {},
  },
  data() {
    return {
      code: "",
      codeError: false,
      genError: false,
      codeRequestId: null,
      webauthnError: false,
      webAuthnSupported: isWebAuthnSupported(),
      working: false,
      selectedDevice: null,
      snackbar: {
        show: false,
        content: "",
        color: "",
      },
    };
  },

  computed: {
    ...mapState({
      otpRequired: "otpRequired",
    }),
    /**
     * All devices awaiting 2FA (ordered by backend: last-used first).
     * WebAuthn credentials are merged into a single entry, because the browser
     * offers all of them in one authentication ceremony.
     */
    allDevices() {
      const devices = [];
      let webauthnAdded = false;
      for (const device of this.otpRequired || []) {
        if (device.device_type === "webauthn") {
          if (webauthnAdded) continue;
          webauthnAdded = true;
          devices.push({ device_type: "webauthn", pk: null });
        } else {
          devices.push(device);
        }
      }
      return devices;
    },
    /** The device type currently selected for verification. */
    activeDeviceType() {
      return this.selectedDevice ? this.selectedDevice.device_type : null;
    },
  },

  methods: {
    ...mapActions({
      logout: "logout",
      finishAuthentication: "finishAuthentication",
    }),

    /** Return a human-readable label for a device in the selector list. */
    deviceLabel(device) {
      if (device.device_type === "email") {
        return device.email || this.$t("resend_verification_code");
      }
      if (device.device_type === "webauthn") {
        return this.$t("webauthn_label");
      }
      return device.name;
    },

    deviceIcon(device) {
      return {
        email: this.$icons.email,
        totp: this.$icons.cellphone,
        webauthn: this.$icons.key,
      }[device.device_type];
    },

    /** Devices of different types can share a pk, so compare both. */
    isSelected(device) {
      return (
        !!this.selectedDevice &&
        this.selectedDevice.pk === device.pk &&
        this.selectedDevice.device_type === device.device_type
      );
    },

    /** Switch the active device and reset state. */
    selectDevice(device) {
      if (this.isSelected(device)) return;
      this.selectedDevice = device;
      this.code = "";
      this.codeError = false;
      this.genError = false;
      this.webauthnError = false;
      this.codeRequestId = null;
      // the lock is per device
      this.otpLockedUntil = null;
      if (device.device_type === "email") {
        this.sendCodeViaEmail();
      }
    },

    async sendCodeViaEmail() {
      this.genError = false;
      this.codeError = false;
      this.working = true;
      let response = await this.http({
        url: `/api/otp/email/${this.selectedDevice.pk}/generate/`,
        method: "post",
        privileged: true,
        dontShowError: true,
      });
      if (response.error) {
        this.genError = true;
      } else {
        this.codeRequestId = response.response.data.request_id;
        this.snackbar = {
          show: true,
          content: this.$t("verification_email_successfully_sent"),
          color: "success",
        };
      }
      this.working = false;
    },

    async verifyEmailCode() {
      this.codeError = false;
      this.genError = false;
      this.working = true;
      let response = await this.http({
        url: `/api/otp/email/${this.selectedDevice.pk}/verify/`,
        method: "post",
        data: { code: this.code },
        privileged: true,
        dontShowError: true,
      });
      if (this.handleOtpThrottle(response.error)) {
        // keep the code - the email token is still valid once the lock expires
      } else if (response.error) {
        this.codeError = true;
      } else {
        // Code was verified successfully -> finish authentication
        this.finishAuthentication();
      }
      this.working = false;
    },

    async verifyWebAuthn() {
      this.webauthnError = false;
      this.working = true;
      try {
        const begin = await this.http({
          url: "/api/otp/webauthn/authentication/begin/",
          method: "post",
          privileged: true,
          dontShowError: true,
        });
        if (begin.error) {
          this.webauthnError = true;
          return;
        }
        // this is where the browser prompts the user to use the authenticator
        const assertion = await webAuthnAuthenticate(begin.response.data);
        const complete = await this.http({
          url: "/api/otp/webauthn/authentication/complete/",
          method: "post",
          data: assertion,
          privileged: true,
          dontShowError: true,
        });
        if (complete.error) {
          this.webauthnError = true;
        } else {
          this.finishAuthentication();
        }
      } catch (error) {
        // dismissing the browser prompt is not a failed verification
        if (!isWebAuthnCancellation(error)) {
          this.webauthnError = true;
        }
      } finally {
        this.working = false;
      }
    },

    async verifyTotpCode() {
      this.codeError = false;
      this.working = true;
      let response = await this.http({
        url: `/api/otp/totp/${this.selectedDevice.pk}/verify/`,
        method: "post",
        data: { code: this.code },
        privileged: true,
        dontShowError: true,
      });
      if (response.error) {
        // a throttled request did not check the code, so it is not reported as wrong
        this.codeError = !this.handleOtpThrottle(response.error);
        this.code = "";
      } else {
        this.finishAuthentication();
      }
      this.working = false;
    },
  },

  async mounted() {
    // Pre-select the first device (backend sorts last-used first),
    // skipping WebAuthn if the browser cannot use it
    if (this.allDevices.length > 0) {
      this.selectedDevice =
        this.allDevices.find(
          (d) => d.device_type !== "webauthn" || this.webAuthnSupported,
        ) ?? this.allDevices[0];
      if (this.selectedDevice.device_type === "email") {
        await this.sendCodeViaEmail();
      }
    }
  },

  watch: {
    code() {
      // automatically send the code if it's complete
      // this is especially effective when the user pastes the code
      if (
        this.code.length === 6 &&
        !this.working &&
        !this.codeError &&
        !this.otpLocked
      ) {
        if (this.activeDeviceType === "email" && this.codeRequestId) {
          this.verifyEmailCode();
        } else if (this.activeDeviceType === "totp") {
          this.verifyTotpCode();
        }
      } else if (this.code.length > 0) {
        this.codeError = false;
      }
    },
  },
};
</script>

<style lang="scss">
.v-select.v-text-field.short input {
  max-width: 0;
}

div.small {
  font-size: 80%;
}
</style>
